"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { ReactRenderer, type Editor } from "@tiptap/react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import Link from "next/link";
import { Globe, ExternalLink, Loader2 } from "lucide-react";
import { getLoreEntryPreview } from "@/lib/actions/wiki-editor-actions";
import { CATEGORY_ICONS, CATEGORY_COLORS } from "@/lib/wiki-config";
type Preview = NonNullable<Awaited<ReturnType<typeof getLoreEntryPreview>>>;
const HOVER_OPEN_DELAY_MS = 250;
const HOVER_CLOSE_DELAY_MS = 200;
const PREVIEW_CACHE_LIMIT = 128;
const previewCache = new Map<string, Promise<Preview | null>>();
function fetchPreview(entryId: string): Promise<Preview | null> {
  const cached = previewCache.get(entryId);
  if (cached) {
    // LRU touch: move to the back of the insertion order.
    previewCache.delete(entryId);
    previewCache.set(entryId, cached);
    return cached;
  }
  const p = getLoreEntryPreview(entryId).catch(() => null);
  previewCache.set(entryId, p);
  // Evict oldest if over budget.
  if (previewCache.size > PREVIEW_CACHE_LIMIT) {
    const oldest = previewCache.keys().next().value;
    if (oldest !== undefined) previewCache.delete(oldest);
  }
  return p;
}

/** Clear the cached preview for an entry — call after the entry is edited. */
export function invalidateMentionPreview(entryId: string) {
  previewCache.delete(entryId);
}

// ─── Card UI ───────────────────────────────────────────

type CardState = {
  kind: "loading";
} | {
  kind: "missing";
} | {
  kind: "ready";
  preview: Preview;
};
function HoverCard(t0) {
  const $ = _c(31);
  const {
    state
  } = t0;
  if (state.kind === "loading") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="mention-hover-card mention-hover-card--loading" role="status" aria-live="polite"><Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /><span className="text-xs text-muted-foreground">Loading…</span></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (state.kind === "missing") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="mention-hover-card mention-hover-card--empty" role="status"><Globe className="h-4 w-4 text-muted-foreground/50" /><span className="text-xs text-muted-foreground">Lore entry no longer exists.</span></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const {
    preview
  } = state;
  const Icon = CATEGORY_ICONS[preview.category] ?? Globe;
  const accent = CATEGORY_COLORS[preview.category]?.accent ?? "text-zinc-500";
  const t1 = `h-3.5 w-3.5 ${accent}`;
  let t2;
  if ($[2] !== Icon || $[3] !== t1) {
    t2 = <Icon className={t1} />;
    $[2] = Icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `text-[10px] font-semibold uppercase tracking-wide ${accent}`;
  let t4;
  if ($[5] !== preview.categoryLabel || $[6] !== t3) {
    t4 = <span className={t3}>{preview.categoryLabel}</span>;
    $[5] = preview.categoryLabel;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="mention-hover-card-header">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== preview.name) {
    t6 = <div className="mention-hover-card-name">{preview.name}</div>;
    $[11] = preview.name;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== preview.description) {
    t7 = preview.description ? <p className="mention-hover-card-description">{preview.description}</p> : null;
    $[13] = preview.description;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== preview.snippet) {
    t8 = preview.snippet ? <p className="mention-hover-card-snippet">{preview.snippet}</p> : null;
    $[15] = preview.snippet;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== preview.description || $[18] !== preview.snippet) {
    t9 = !preview.description && !preview.snippet ? <p className="mention-hover-card-empty-text">No details yet — open the Wiki to add some.</p> : null;
    $[17] = preview.description;
    $[18] = preview.snippet;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `/wiki/${preview.wikiId}`;
  let t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ExternalLink className="h-3 w-3" />;
    t12 = <span>Open in Wiki</span>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10) {
    t13 = <Link href={t10} prefetch={false} className="mention-hover-card-link">{t11}{t12}</Link>;
    $[22] = t10;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t14 = <div className="mention-hover-card" role="tooltip">{t5}{t6}{t7}{t8}{t9}{t13}</div>;
    $[24] = t13;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}

// ─── Hook ──────────────────────────────────────────────

/**
 * Show a tippy preview card when the user hovers (or focuses) a `.lore-mention`
 * chip inside the given Tiptap editor. Uses event delegation on the editor DOM
 * so dynamic mentions are picked up without rebinding.
 *
 * Mentions are rendered by @tiptap/extension-mention as plain spans, not React
 * NodeViews, so we render the card into a ReactRenderer-owned node and let
 * tippy position it. ReactRenderer tracks mount/unmount per editor.
 */
export function useMentionHover(editor, t0) {
  const $ = _c(4);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== enabled) {
    t1 = () => {
      if (!editor || !enabled) {
        return;
      }
      const activeEditor = editor;
      const container = activeEditor.view.dom as HTMLElement;
      let renderer = null;
      let popup = null;
      let activeId = null;
      let openTimer = null;
      let closeTimer = null;
      const clearOpenTimer = function clearOpenTimer() {
        if (openTimer) {
          clearTimeout(openTimer);
          openTimer = null;
        }
      };
      const clearCloseTimer = function clearCloseTimer() {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      };
      const teardown = function teardown() {
        clearOpenTimer();
        clearCloseTimer();
        const p = popup;
        popup = null;
        p?.destroy();
        renderer?.destroy();
        renderer = null;
        activeId = null;
      };
      const render = function render(state) {
        renderer?.updateProps({
          state
        });
      };
      const open = function open(target, entryId) {
        activeId = entryId;
        renderer = new ReactRenderer(HoverCard, {
          props: {
            state: {
              kind: "loading"
            } satisfies CardState
          },
          editor: activeEditor
        });
        popup = tippy(target, {
          content: renderer.element,
          appendTo: _temp,
          interactive: true,
          interactiveBorder: 12,
          placement: "top",
          trigger: "manual",
          hideOnClick: true,
          offset: [0, 8],
          arrow: false,
          animation: "shift-away-subtle",
          maxWidth: 360
        });
        const card = renderer.element as HTMLElement;
        card.addEventListener("pointerenter", clearCloseTimer);
        card.addEventListener("pointerleave", requestClose);
        popup.show();
        fetchPreview(entryId).then(preview => {
          if (activeId !== entryId || !renderer) {
            return;
          }
          render(preview ? {
            kind: "ready",
            preview
          } : {
            kind: "missing"
          });
        });
      };
      const requestOpen = function requestOpen(target_0, entryId_0) {
        if (activeId === entryId_0 && popup) {
          clearCloseTimer();
          return;
        }
        teardown();
        openTimer = setTimeout(() => {
          openTimer = null;
          open(target_0, entryId_0);
        }, HOVER_OPEN_DELAY_MS);
      };
      function requestClose() {
        clearOpenTimer();
        clearCloseTimer();
        closeTimer = setTimeout(() => {
          closeTimer = null;
          teardown();
        }, HOVER_CLOSE_DELAY_MS);
      }
      const mentionFrom = function mentionFrom(el) {
        if (!(el instanceof Element)) {
          return null;
        }
        return el.closest(".lore-mention");
      };
      const handleOver = function handleOver(event) {
        const mention = mentionFrom(event.target);
        if (!mention) {
          return;
        }
        const entryId_1 = mention.getAttribute("data-id");
        if (!entryId_1) {
          return;
        }
        requestOpen(mention, entryId_1);
      };
      const handleOut = function handleOut(event_0) {
        const mention_0 = mentionFrom(event_0.target);
        if (!mention_0) {
          return;
        }
        const related = event_0.relatedTarget;
        if (related instanceof Element) {
          if (mention_0.contains(related) || related.closest(".tippy-box")) {
            return;
          }
        }
        requestClose();
      };
      const handleFocusIn = function handleFocusIn(event_1) {
        const mention_1 = mentionFrom(event_1.target);
        if (!mention_1) {
          return;
        }
        const entryId_2 = mention_1.getAttribute("data-id");
        if (!entryId_2) {
          return;
        }
        clearOpenTimer();
        if (activeId === entryId_2 && popup) {
          return;
        }
        teardown();
        open(mention_1, entryId_2);
      };
      const handleFocusOut = function handleFocusOut(event_2) {
        const mention_2 = mentionFrom(event_2.target);
        if (!mention_2) {
          return;
        }
        const related_0 = event_2.relatedTarget;
        if (related_0 instanceof Element && related_0.closest(".tippy-box")) {
          return;
        }
        requestClose();
      };
      const handleKey = function handleKey(event_3) {
        if (event_3.key === "Escape" && popup) {
          teardown();
        }
      };
      container.addEventListener("mouseover", handleOver);
      container.addEventListener("mouseout", handleOut);
      container.addEventListener("focusin", handleFocusIn);
      container.addEventListener("focusout", handleFocusOut);
      container.addEventListener("keydown", handleKey);
      return () => {
        container.removeEventListener("mouseover", handleOver);
        container.removeEventListener("mouseout", handleOut);
        container.removeEventListener("focusin", handleFocusIn);
        container.removeEventListener("focusout", handleFocusOut);
        container.removeEventListener("keydown", handleKey);
        teardown();
      };
    };
    t2 = [editor, enabled];
    $[0] = editor;
    $[1] = enabled;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
}
function _temp() {
  return document.body;
}